import {
  CANCEL_ORTHOPHOTO,
  COMPLETE_ORTHOPHOTO,
  CREATE_ORTHOPHOTO,
} from '@/services/api/master/orthophoto'
import i18n from '@/i18n'
import {
  ORTHOPHOTO_CHUNK_SIZE,
  abortUploadRequest,
  clearUploadController,
  registerUploadController,
  uploadOrthophotoChunk,
} from '@/services/api/master/orthophoto-upload'
import {
  deleteUploadFileHandle,
  getUploadFileHandle,
  saveUploadFileHandle,
} from '@/services/upload/file-handle-store'

const validateResumeFile = (file, upload) =>
  file.name === upload.fileName &&
  file.size === upload.fileSize &&
  (!upload.fileLastModified || file.lastModified === upload.fileLastModified)

const createUserError = (key) =>
  Object.assign(new Error(i18n.t(key)), { isUserError: true })

const uploadParts = async ({
  uploadId,
  file,
  token,
  totalParts,
  chunkSize,
  nextPartNumber,
  initialParts = [],
  update,
}) => {
  const parts = [...initialParts]

  for (let partNumber = nextPartNumber; partNumber <= totalParts; partNumber++) {
    const start = (partNumber - 1) * chunkSize
    const end = Math.min(start + chunkSize, file.size)
    const controller = new AbortController()
    registerUploadController(uploadId, controller)

    let etag
    try {
      etag = await uploadOrthophotoChunk({
        token,
        partNumber,
        chunk: file.slice(start, end),
        signal: controller.signal,
      })
    } finally {
      clearUploadController(uploadId, controller)
    }

    const existingPartIndex = parts.findIndex((part) => part.partNumber === partNumber)
    const uploadedPart = { partNumber, etag }
    if (existingPartIndex >= 0) parts.splice(existingPartIndex, 1, uploadedPart)
    else parts.push(uploadedPart)

    update({
      parts: [...parts],
      nextPartNumber: partNumber + 1,
      uploadedBytes: end,
      progress: Math.round((end / file.size) * 100),
    })
  }

  return parts
}

const completeUpload = async ({ apollo, orthophotoId, token, parts }) => {
  const response = await apollo.mutate({
    mutation: COMPLETE_ORTHOPHOTO,
    variables: { input: { orthophotoId, token, parts } },
  })
  const result = response.data.completeUploadOrthophoto
  if (!result.isSuccessful) {
    throw createUserError('master.orthophoto.upload.messages.completeFailed')
  }
  return result.completeUploadOrthophoto
}

const start = async ({
  uploadId,
  file,
  fileHandle,
  apollo,
  update,
  complete,
  onCreated,
}) => {
  let orthophotoId
  let token

  try {
    if (fileHandle) {
      try {
        await saveUploadFileHandle(uploadId, fileHandle)
      } catch {
        update({ resumeMode: 'file-picker' })
      }
    }

    const totalParts = Math.ceil(file.size / ORTHOPHOTO_CHUNK_SIZE)
    const createResponse = await apollo.mutate({
      mutation: CREATE_ORTHOPHOTO,
      variables: {
        input: {
          label: file.name,
          sourcePlatform: 'S3',
          sourceType: 'TIFF',
          totalFileParts: totalParts,
        },
      },
    })
    const createResult = createResponse.data.createOrthophoto
    if (!createResult.isSuccessful) {
      throw createUserError('master.orthophoto.upload.messages.createFailed')
    }

    const orthophoto = createResult.createOrthophoto
    orthophotoId = orthophoto.orthophotoId
    token = orthophoto.token
    if (!token) {
      throw createUserError('master.orthophoto.upload.messages.tokenNotFound')
    }

    update({
      orthophotoId,
      token,
      parts: [],
      totalParts,
      nextPartNumber: 1,
      uploadedBytes: 0,
      chunkSize: ORTHOPHOTO_CHUNK_SIZE,
    })
    if (onCreated) onCreated({ orthophotoId })

    const parts = await uploadParts({
      uploadId,
      file,
      token,
      totalParts,
      chunkSize: ORTHOPHOTO_CHUNK_SIZE,
      nextPartNumber: 1,
      update,
    })

    update({ status: 'processing' })
    const result = await completeUpload({ apollo, orthophotoId, token, parts })
    complete()
    await deleteUploadFileHandle(uploadId).catch(() => undefined)
    return result
  } catch (error) {
    error.isResumable = Boolean(orthophotoId && token)
    throw error
  }
}

const cancel = async ({ upload, apollo }) => {
  abortUploadRequest(upload.id)
  try {
    if (!upload.orthophotoId || !upload.token) return

    const response = await apollo.mutate({
      mutation: CANCEL_ORTHOPHOTO,
      variables: {
        input: {
          orthophotoId: upload.orthophotoId,
          token: upload.token,
        },
      },
    })
    const result = response.data.cancelUploadOrthophoto
    if (!result.isSuccessful) {
      throw new Error(i18n.t('master.orthophoto.upload.messages.cancelFailed'))
    }
  } finally {
    await deleteUploadFileHandle(upload.id).catch(() => undefined)
  }
}

const pause = ({ upload }) => abortUploadRequest(upload.id)
const cleanup = (upload) => deleteUploadFileHandle(upload.id).catch(() => undefined)

const restoreFile = async ({ upload, requestPermission = false }) => {
  const fileHandle = await getUploadFileHandle(upload.id).catch(() => null)
  if (!fileHandle) return null

  let permission = await fileHandle.queryPermission({ mode: 'read' })
  if (permission === 'prompt' && requestPermission) {
    permission = await fileHandle.requestPermission({ mode: 'read' })
  }
  if (permission !== 'granted') return null

  const file = await fileHandle.getFile()
  return validateResumeFile(file, upload) ? file : null
}

const resume = async ({ upload, file, apollo, update }) => {
  const chunkSize = upload.chunkSize || ORTHOPHOTO_CHUNK_SIZE
  const initialParts = Array.isArray(upload.parts) ? upload.parts : []
  const parts = await uploadParts({
    uploadId: upload.id,
    file,
    token: upload.token,
    totalParts: upload.totalParts,
    chunkSize,
    nextPartNumber: upload.nextPartNumber || initialParts.length + 1,
    initialParts,
    update,
  })

  update({ status: 'processing' })
  const result = await completeUpload({
    apollo,
    orthophotoId: upload.orthophotoId,
    token: upload.token,
    parts,
  })
  await deleteUploadFileHandle(upload.id).catch(() => undefined)
  return result
}

export default {
  type: 'orthophoto',
  accept: '.tif',
  canHandle: (upload) => Boolean(upload?.orthophotoId && upload?.token),
  canResume: (upload) =>
    Boolean(
      upload?.orthophotoId &&
        upload?.token &&
        upload?.totalParts &&
        upload?.nextPartNumber &&
        Array.isArray(upload?.parts)
    ),
  validateResumeFile,
  restoreFile,
  start,
  resume,
  pause,
  cancel,
  cleanup,
}
